import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';
import { Form, Formik, FormikProps } from 'formik';
import i18n from '../../utilities/i18n';
import { ROUTES } from '../../constants';
import mediator from '../../utilities/mediator';
import log from '../../utilities/log';
import TextInputWithError from '../ui/forms/TextInputWithError';
import {
  Button,
  Card,
  Highlight,
  HStack,
  IconBannersWarning,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import { ConfirmationModal } from '../modal/ConfirmationModal';
import PreventSubmitWrapper from '../ui/forms/PreventSubmitWrapper';

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string().required(i18n.t('Auth_ForgotPassword.emailRequired')),
});

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const initialValues = {
    email: '',
  };
  const validationSchema = forgotPasswordSchema;
  const onSubmit = (values) => {
    setHasErrors(false);
    return new Promise<void>((resolve): void => {
      mediator
        .request<void>('auth:forgotPassword', values)
        .then(() => {
          resolve();
          navigate(ROUTES.ROOT);
        })
        .catch((err) => {
          log.error(err);
          setHasErrors(true);
          resolve();
        });
    });
  };

  const navigateToLoginScreen = () => {
    navigate(ROUTES.AUTH.LOGIN);
  };

  return (
    <VStack alignSelf="center" margin="auto">
      <Card>
        <VStack margin="$4">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<any>) => (
              <Form noValidate id="cv_auth_forgot_password">
                <VStack gap="$4">
                  <VStack alignItems="center">
                    <Text type="$h2.strong">
                      {i18n.t('Auth_ForgotPassword.title')}
                    </Text>

                    <Text type="$h4.default">
                      {i18n.t('Auth_ForgotPassword.instructions')}
                    </Text>
                  </VStack>

                  {hasErrors ? (
                    <VStack marginBottom={'$4'}>
                      <Highlight
                        variant="danger"
                        iconStart={
                          <IconBannersWarning
                            color="$neutral.0"
                            accentColor="$support.danger.default"
                          />
                        }
                        title={i18n.t('Common.errorGenericTitle')}
                        body={i18n.t('Auth_ForgotPassword.somethingWrong')}
                      />
                    </VStack>
                  ) : null}

                  <TextInputWithError
                    name="email"
                    label={i18n.t('Auth_ForgotPassword.email')}
                  />
                  <PreventSubmitWrapper>
                    <HStack justifyContent="center" gap="$4">
                      <Button
                        onPress={() => setConfirmationModalOpen(true)}
                        label={i18n.t('Common.submit')}
                        disabled={
                          formikProps.isSubmitting ||
                          !formikProps.isValid ||
                          formikProps.values.email === ''
                        }
                        variant="primary"
                        testID="forgot-password-submit-button"
                      />
                      <Button
                        onPress={navigateToLoginScreen}
                        label={i18n.t('Common.cancel')}
                        variant="secondary"
                        testID="forgot-password-cancel-button"
                      />
                    </HStack>
                  </PreventSubmitWrapper>

                  <ConfirmationModal
                    title={i18n.t('Auth_ForgotPassword.emailSentTitle')}
                    description={i18n.t('Auth_ForgotPassword.emailSent')}
                    isOpen={confirmationModalOpen}
                    onCancel={() => setConfirmationModalOpen(false)}
                    onConfirm={formikProps.submitForm}
                  />
                </VStack>
              </Form>
            )}
          </Formik>
        </VStack>
      </Card>
    </VStack>
  );
}
